package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.domain.leave.LeaveBalances
import com.evoapps.evoshift.domain.model.LeaveBalance
import com.evoapps.evoshift.domain.model.LeaveEntitlement
import com.evoapps.evoshift.domain.usecase.leave.GetMyLeaveEntitlementUseCase
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.LimitUnit
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.domain.model.RequestPlace
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.repository.WorkRequestRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.RequestCounter
import com.evoapps.evoshift.domain.requests.RequestCounters
import com.evoapps.evoshift.domain.usecase.requests.SubmitWorkRequestUseCase
import com.evoapps.evoshift.domain.usecase.requests.WorkPlacesUseCase

data class AbsencesUiState(
    val isLoading: Boolean = false,
    val userId: String = "",
    val workplaceId: String? = null,
    /** What the person may do at this workplace: the workplace's absences with APPROVE_REQUESTS, own ones with WORK_SHIFTS. */
    val capabilities: Set<Capability> = emptySet(),
    val activeAbsences: List<Absence> = emptyList(),
    val pastAbsences: List<Absence> = emptyList(),
    val error: UiText? = null,
    val isSubmitting: Boolean = false,
    /** SH-113: the person's own leave entitlement this year (not for managers deciding the team's). */
    val leaveEntitlement: LeaveEntitlement? = null,
    /** SH-223: remote work, work events and overtime: the person's own (managers: the workplace's). */
    val workRequests: List<WorkRequest> = emptyList(),
    /** SH-223: the workplace's shared places to work from. */
    val places: List<RequestPlace> = emptyList(),
    /** SH-223: the workplace's numbers for the request types (blood donation days, yearly limits). */
    val requestSettings: RequestSettings = RequestSettings(),
    val market: Market = Market.POLAND,
    /** SH-223: the place just added from the request form, for it to be chosen there. */
    val addedPlace: RequestPlace? = null,
    /** SH-223: the person's own pay terms here (their contract on a day decides the types offered); null: unknown. */
    val financial: com.evoapps.evoshift.domain.model.FinancialEngagement? = null
) {
    /** SH-223: the picker's order of the types offered on [date]: the person's most used of the last year on top. */
    fun pickerOrder(date: String): com.evoapps.evoshift.domain.requests.RequestOrdering.Ordered =
        com.evoapps.evoshift.domain.requests.RequestOrdering.of(
            offeredTypes(date),
            allAbsences.filter { it.userId == userId }.map { it.type.name to it.startDate } +
                workRequests.filter { it.userId == userId }.map { it.kind.name to it.startDate },
            AppClock.today()
        )

    /** SH-223: the types offered on [date] ("YYYY-MM-DD"): the workplace's rules and the person's contract that day. */
    fun offeredTypes(date: String): List<com.evoapps.evoshift.domain.requests.RequestTypeInfo> =
        com.evoapps.evoshift.domain.requests.RequestEligibility.offered(requestSettings, financial, date)

    private val rules: PayrollRules get() = PayrollRules.of(market)
    private val allAbsences: List<Absence> get() = activeAbsences + pastAbsences

    /** SH-223: the places in picking order, the person's recent ones first. */
    val orderedPlaces: List<RequestPlace> get() = WorkPlacesUseCase.ordered(places, userId, workRequests)
    val recentPlaceCount: Int get() = WorkPlacesUseCase.recentCount(places, userId, workRequests)

    /** SH-223: the person's counter of [type] in [year] (null: no yearly limit); [unitIfNew] before the year's first request. */
    fun counter(type: AbsenceType, year: Int, unitIfNew: LimitUnit? = null): RequestCounter? =
        RequestCounters.counter(type, userId, workplaceId, year, allAbsences, requestSettings, rules, unitIfNew)

    /** SH-223: the care allowance's counters (children, other family, in all), as hints. */
    fun careCounters(year: Int): List<RequestCounter> = RequestCounters.careAllowance(userId, workplaceId, year, allAbsences, requestSettings, rules)

    /** SH-223: the approved overtime still to take as time off. */
    val overtimeBalance: RequestCounter?
        get() = workplaceId?.let { RequestCounters.overtimeBalance(userId, it, workRequests, allAbsences, rules) }

    /** SH-223: whether [draft] fits its limits (what the form says before sending). */
    fun check(draft: Absence): LimitCheck =
        RequestCounters.check(draft.copy(userId = userId, workplaceId = workplaceId), allAbsences, requestSettings, rules, workRequests)

    /** SH-223: whether the year's unit of [type] is set already (by its first request of [year]). */
    fun yearUnit(type: AbsenceType, year: Int): LimitUnit? = RequestCounters.yearUnit(type, userId, workplaceId, year, allAbsences)

    /** SH-223: whether a yearly limit has anything against it in [year], or approved overtime waits to be taken. */
    fun hasLimitsInUse(year: Int): Boolean =
        listOf(AbsenceType.CHILDCARE, AbsenceType.FORCE_MAJEURE, AbsenceType.CARER_LEAVE).any { type ->
            counter(type, year)?.let { it.used + it.pending > 0 } == true
        } || (overtimeBalance?.limit ?: 0.0) > 0

    /** SH-223: the work requests still pending or not over yet, and the rest. */
    val activeWork: List<WorkRequest> get() = workRequests.filter { it.status == AbsenceStatus.PENDING || it.endDate >= AppClock.today().toString() }
    val pastWork: List<WorkRequest> get() = workRequests.filterNot { it in activeWork }

    /** SH-113: the balance with the absences on screen, so a new or withdrawn request counts at once. */
    val leaveBalance: LeaveBalance?
        get() = leaveEntitlement?.let { LeaveBalances.balance(it, activeAbsences + pastAbsences) }
}

sealed interface AbsencesEvent {
    data object Refresh : AbsencesEvent
    data class CreateAbsence(
        val type: AbsenceType,
        val startDate: String,
        val endDate: String,
        val reason: String?,
        /** SH-57: sick leave paid at 100% (as the sick note says). */
        val sickPayFull: Boolean = false
    ) : AbsencesEvent
    /** SH-223: any absence type with its details ([draft]'s user and workplace are filled in here). */
    data class CreateRequest(val draft: Absence) : AbsencesEvent
    /** SH-223: remote work, a work event or overtime. */
    data class CreateWorkRequest(val draft: WorkRequest) : AbsencesEvent
    /** SH-223: the person withdraws their pending work request. */
    data class CancelWorkRequest(val requestId: String) : AbsencesEvent
    /** SH-223: a new place on the workplace's shared list. */
    data class AddPlace(val name: String) : AbsencesEvent
    data object ConsumeAddedPlace : AbsencesEvent
    data class UpdateStatus(val absence: Absence, val status: AbsenceStatus) : AbsencesEvent
    data class DeleteAbsence(val absenceId: String) : AbsencesEvent
    data object ClearError : AbsencesEvent
}

class AbsencesViewModel(
    private val absenceRepository: AbsenceRepository,
    private val reportAbsenceUseCase: ReportAbsenceUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val getLeaveEntitlement: GetMyLeaveEntitlementUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) },
    /** SH-223: work requests and places; without them only absences. */
    private val workRequestRepository: WorkRequestRepository? = null,
    private val submitWorkRequest: SubmitWorkRequestUseCase? = null,
    private val workPlaces: WorkPlacesUseCase? = workRequestRepository?.let { WorkPlacesUseCase(it) },
    /** SH-223: the workplace's request settings and market; without it the defaults. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** SH-223: the person's own pay terms (their contract), for the types offered. */
    private val userRepository: com.evoapps.evoshift.domain.repository.UserRepository? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(AbsencesUiState())
    val uiState: StateFlow<AbsencesUiState> = _uiState.asStateFlow()

    init {
        observeSession()
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                val user = getCurrentUserUseCase().first()
                if (user != null && workplaceId != null) {
                    val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
                    _uiState.update { state ->
                        // Another workplace's absences must not stay on screen while these load.
                        val switched = state.workplaceId != workplaceId
                        state.copy(
                            userId = user.uid,
                            workplaceId = workplaceId,
                            capabilities = capabilities,
                            activeAbsences = if (switched) emptyList() else state.activeAbsences,
                            pastAbsences = if (switched) emptyList() else state.pastAbsences,
                            leaveEntitlement = if (switched) null else state.leaveEntitlement,
                            workRequests = if (switched) emptyList() else state.workRequests,
                            places = if (switched) emptyList() else state.places
                        )
                    }
                    loadAbsences()
                    loadLeaveEntitlement()
                    loadRequestFacts()
                }
            }
        }
    }

    fun onEvent(event: AbsencesEvent) {
        when (event) {
            AbsencesEvent.Refresh -> {
                loadAbsences()
                loadRequestFacts()
            }
            is AbsencesEvent.CreateAbsence -> if (shows(Gate.REPORT_ABSENCE)) createRequest(
                Absence(
                    id = "", userId = "", workplaceId = null, type = event.type, status = AbsenceStatus.PENDING,
                    startDate = event.startDate, endDate = event.endDate, reason = event.reason, attachmentUrl = null,
                    processedBy = null, processedAt = null, metadata = AuditMetadata(createdAt = "", createdBy = ""), sickPayFull = event.sickPayFull
                )
            )
            is AbsencesEvent.CreateRequest -> if (shows(Gate.REPORT_ABSENCE)) createRequest(event.draft)
            is AbsencesEvent.CreateWorkRequest -> if (shows(Gate.REPORT_ABSENCE)) createWorkRequest(event.draft)
            is AbsencesEvent.CancelWorkRequest -> if (shows(Gate.REPORT_ABSENCE)) cancelWorkRequest(event.requestId)
            is AbsencesEvent.AddPlace -> if (shows(Gate.REPORT_ABSENCE)) addPlace(event.name)
            AbsencesEvent.ConsumeAddedPlace -> _uiState.update { it.copy(addedPlace = null) }
            is AbsencesEvent.UpdateStatus -> if (shows(Gate.DECIDE_REQUESTS)) updateStatus(event.absence, event.status)
            is AbsencesEvent.DeleteAbsence -> if (shows(Gate.REPORT_ABSENCE)) deleteAbsence(event.absenceId)
            AbsencesEvent.ClearError -> _uiState.update { it.copy(error = null) }
        }
    }

    private fun shows(gate: Gate): Boolean = _uiState.value.capabilities.shows(gate)

    private fun loadAbsences() {
        val workplaceId = _uiState.value.workplaceId ?: return
        val userId = _uiState.value.userId
        val capabilities = _uiState.value.capabilities
        // An ended engagement reads nothing here.
        if (capabilities.isEmpty()) {
            _uiState.update { it.copy(isLoading = false, activeAbsences = emptyList(), pastAbsences = emptyList()) }
            return
        }

        viewModelScope.launch {
            // Only the first load shows a spinner; later ones refresh what is on screen.
            _uiState.update { it.copy(isLoading = it.activeAbsences.isEmpty() && it.pastAbsences.isEmpty()) }

            val result = if (capabilities.shows(Gate.TEAM_REQUESTS)) {
                absenceRepository.getAbsencesForWorkplace(workplaceId)
            } else {
                absenceRepository.getAbsencesForUser(userId)
            }

            result.fold(
                onSuccess = { allAbsences ->
                    setAbsences(allAbsences)
                    _uiState.update { it.copy(isLoading = false, error = null) }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
            )
        }
    }

    /** SH-113: the person's own entitlement this year, for the balance card and the request dialog. */
    private fun loadLeaveEntitlement() {
        val use = getLeaveEntitlement ?: return
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (!state.capabilities.shows(Gate.OWN_ABSENCES) || state.capabilities.shows(Gate.TEAM_REQUESTS)) return
        viewModelScope.launch {
            use(state.userId, workplaceId, today().year).onSuccess { found ->
                _uiState.update { if (it.workplaceId == workplaceId) it.copy(leaveEntitlement = found) else it }
            }
        }
    }

    private fun createRequest(draft: Absence) {
        val workplaceId = _uiState.value.workplaceId ?: return
        val userId = _uiState.value.userId
        val before = allAbsences()
        val request = draft.copy(
            userId = userId, workplaceId = workplaceId, status = AbsenceStatus.PENDING,
            sickPayFull = draft.sickPayFull && (draft.type == AbsenceType.SICK_LEAVE || draft.type == AbsenceType.QUARANTINE),
            metadata = AuditMetadata(createdAt = now().toString(), createdBy = userId)
        )
        // Listed at once as pending, under a placeholder id until the repository returns the real one.
        val pendingId = "$PENDING_ID_PREFIX${draft.type}-${draft.startDate}-${draft.endDate}"
        setAbsences(before + request.copy(id = pendingId))

        viewModelScope.launch {
            _uiState.update { it.copy(isSubmitting = true) }
            reportAbsenceUseCase.submit(request).fold(
                onSuccess = { id ->
                    setAbsences(allAbsences().map { if (it.id == pendingId) it.copy(id = id) else it })
                    _uiState.update { it.copy(isSubmitting = false) }
                    loadAbsences()
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isSubmitting = false) }
                    rollBack(before, error)
                }
            )
        }
    }

    /** SH-223: the person's work requests, the workplace's places and request settings. */
    private fun loadRequestFacts() {
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        if (state.capabilities.isEmpty()) return
        viewModelScope.launch {
            workplaceRepository?.getWorkplace(workplaceId)?.getOrNull()?.let { workplace ->
                _uiState.update {
                    if (it.workplaceId == workplaceId) it.copy(requestSettings = workplace.settings.requests, market = workplace.settings.market) else it
                }
            }
        }
        // SH-223: their own contract (a manager deciding the team's needs none).
        if (!state.capabilities.shows(Gate.TEAM_REQUESTS)) userRepository?.let { users ->
            viewModelScope.launch {
                val financial = users.getSensitiveUserData(state.userId).getOrNull()?.financialEngagements?.get(workplaceId)
                _uiState.update { if (it.workplaceId == workplaceId) it.copy(financial = financial) else it }
            }
        }
        val repository = workRequestRepository ?: return
        viewModelScope.launch {
            val team = state.capabilities.shows(Gate.TEAM_REQUESTS)
            val found = (if (team) repository.forWorkplace(workplaceId) else repository.forUser(state.userId)).getOrNull()
                ?.filter { it.workplaceId == workplaceId }
            val places = workPlaces?.list(workplaceId)?.getOrNull()
            _uiState.update {
                if (it.workplaceId != workplaceId) it
                else it.copy(workRequests = found ?: it.workRequests, places = places ?: it.places)
            }
        }
    }

    private fun createWorkRequest(draft: WorkRequest) {
        val submit = submitWorkRequest ?: return
        val workplaceId = _uiState.value.workplaceId ?: return
        val userId = _uiState.value.userId
        val request = draft.copy(userId = userId, workplaceId = workplaceId, status = AbsenceStatus.PENDING,
            metadata = AuditMetadata(createdAt = now().toString(), createdBy = userId))
        val pendingId = "$PENDING_ID_PREFIX${draft.kind}-${draft.startDate}-${draft.endDate}"
        val before = _uiState.value.workRequests
        _uiState.update { it.copy(workRequests = listOf(request.copy(id = pendingId)) + it.workRequests, isSubmitting = true) }
        viewModelScope.launch {
            submit(request).fold(
                onSuccess = { id ->
                    _uiState.update { state -> state.copy(isSubmitting = false, workRequests = state.workRequests.map { if (it.id == pendingId) it.copy(id = id) else it }) }
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isSubmitting = false, workRequests = before, error = error.toUiText()) }
                }
            )
        }
    }

    private fun cancelWorkRequest(requestId: String) {
        val repository = workRequestRepository ?: return
        if (requestId.isPending) return
        val before = _uiState.value.workRequests
        if (before.firstOrNull { it.id == requestId }?.status != AbsenceStatus.PENDING) return
        _uiState.update { it.copy(workRequests = before.filter { r -> r.id != requestId }) }
        viewModelScope.launch {
            repository.delete(requestId).onFailure { error -> _uiState.update { it.copy(workRequests = before, error = error.toUiText()) } }
        }
    }

    private fun addPlace(name: String) {
        val places = workPlaces ?: return
        val state = _uiState.value
        val workplaceId = state.workplaceId ?: return
        viewModelScope.launch {
            places.add(workplaceId, name, state.userId, state.places).fold(
                onSuccess = { place ->
                    _uiState.update {
                        it.copy(places = if (it.places.any { p -> p.id == place.id }) it.places else it.places + place, addedPlace = place)
                    }
                },
                onFailure = { error -> _uiState.update { it.copy(error = error.toUiText()) } }
            )
        }
    }

    private fun updateStatus(absence: Absence, status: AbsenceStatus) {
        if (absence.id.isPending) return
        val userId = _uiState.value.userId
        val updatedAbsence = absence.copy(
            status = status,
            processedBy = userId,
            processedAt = now().toString()
        )
        val before = allAbsences()
        setAbsences(before.map { if (it.id == absence.id) updatedAbsence else it })
        viewModelScope.launch {
            absenceRepository.updateAbsence(updatedAbsence).fold(
                onSuccess = { loadAbsences() },
                onFailure = { error -> rollBack(before, error) }
            )
        }
    }

    private fun deleteAbsence(absenceId: String) {
        if (absenceId.isPending) return
        val before = allAbsences()
        // A decided absence stays (firestore.rules): only those who decide requests remove it.
        val absence = before.find { it.id == absenceId }
        if (absence != null && absence.status != AbsenceStatus.PENDING &&
            Capability.APPROVE_REQUESTS !in _uiState.value.capabilities
        ) return
        setAbsences(before.filter { it.id != absenceId })
        viewModelScope.launch {
            absenceRepository.deleteAbsence(absenceId).fold(
                onSuccess = { loadAbsences() },
                onFailure = { error -> rollBack(before, error) }
            )
        }
    }

    private fun allAbsences(): List<Absence> = _uiState.value.let { it.activeAbsences + it.pastAbsences }

    /** Splits into active (pending or not over yet) and past, newest first. */
    private fun setAbsences(allAbsences: List<Absence>) {
        val now = now().toString() // Simplified date comparison
        val active = allAbsences.filter { it.endDate >= now || it.status == AbsenceStatus.PENDING }
        val past = allAbsences.filter { it.endDate < now && it.status != AbsenceStatus.PENDING }
        _uiState.update {
            it.copy(
                activeAbsences = active.sortedByDescending { a -> a.startDate },
                pastAbsences = past.sortedByDescending { a -> a.startDate }
            )
        }
    }

    /** Undoes an optimistic change whose save failed, and says why. */
    private fun rollBack(before: List<Absence>, e: Throwable) {
        setAbsences(before)
        _uiState.update { it.copy(error = e.toUiText()) }
    }
}
